import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Building2,
  Heart,
  Activity,
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';

const SystemAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [analytics, setAnalytics] = useState({
    totalUsers: 1247,
    totalTenants: 12,
    totalRecordings: 8934,
    totalPatients: 456,
    growthMetrics: {
      usersGrowth: 12.5,
      tenantsGrowth: 8.3,
      recordingsGrowth: 23.7,
      patientsGrowth: 15.2
    }
  });

  const [chartData, setChartData] = useState({
    userGrowth: [
      { date: '2024-01-08', users: 1180 },
      { date: '2024-01-09', users: 1195 },
      { date: '2024-01-10', users: 1210 },
      { date: '2024-01-11', users: 1225 },
      { date: '2024-01-12', users: 1235 },
      { date: '2024-01-13', users: 1240 },
      { date: '2024-01-14', users: 1247 }
    ],
    recordingsByTenant: [
      { tenant: 'KadelLabs Hospital', recordings: 2450, percentage: 27.4 },
      { tenant: 'City Medical Center', recordings: 1890, percentage: 21.2 },
      { tenant: 'Downtown Clinic', recordings: 1560, percentage: 17.5 },
      { tenant: 'Regional Healthcare', recordings: 1320, percentage: 14.8 },
      { tenant: 'Others', recordings: 1714, percentage: 19.1 }
    ],
    dailyActivity: [
      { date: '2024-01-08', recordings: 127, logins: 89 },
      { date: '2024-01-09', recordings: 145, logins: 95 },
      { date: '2024-01-10', recordings: 134, logins: 102 },
      { date: '2024-01-11', recordings: 156, logins: 87 },
      { date: '2024-01-12', recordings: 142, logins: 98 },
      { date: '2024-01-13', recordings: 167, logins: 104 },
      { date: '2024-01-14', recordings: 152, logins: 91 }
    ]
  });

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  const MetricCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          <div className="flex items-center mt-2">
            <TrendingUp className={`h-4 w-4 mr-1 ${change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`text-sm font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? '+' : ''}{change}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs last period</span>
          </div>
        </div>
        <div className={`p-3 rounded-lg bg-[#207CE5]`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  const SimpleChart = ({ data, title, type = 'line' }) => {
    const maxValue = Math.max(...data.map(d => d.recordings || d.users || 0));
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button className="text-gray-400 hover:text-gray-600">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        
        {type === 'bar' ? (
          <div className="space-y-4">
            {data.map((item, index) => (
              <div key={index} className="flex items-center">
                <div className="w-32 text-sm text-gray-600 truncate">{item.tenant}</div>
                <div className="flex-1 mx-4">
                  <div className="bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-[#207CE5] h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(item.recordings / maxValue) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-20 text-sm font-medium text-gray-900 text-right">
                  {item.recordings}
                </div>
                <div className="w-16 text-xs text-gray-500 text-right">
                  {item.percentage}%
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-64 flex items-end space-x-2">
            {data.map((item, index) => {
              const value = item.recordings || item.users || 0;
              const height = (value / maxValue) * 100;
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-[#207CE5] rounded-t transition-all duration-300 min-h-[4px]"
                    style={{ height: `${height}%` }}
                  ></div>
                  <div className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                    {new Date(item.date).getDate()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Analytics</h1>
          <p className="text-gray-600">Platform usage insights and performance metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            {timeRangeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics.totalUsers}
          change={analytics.growthMetrics.usersGrowth}
          icon={Users}
          color="bg-blue-500"
        />
        <MetricCard
          title="Active Tenants"
          value={analytics.totalTenants}
          change={analytics.growthMetrics.tenantsGrowth}
          icon={Building2}
          color="bg-purple-500"
        />
        <MetricCard
          title="Total Recordings"
          value={analytics.totalRecordings}
          change={analytics.growthMetrics.recordingsGrowth}
          icon={Heart}
          color="bg-emerald-500"
        />
        <MetricCard
          title="Total Patients"
          value={analytics.totalPatients}
          change={analytics.growthMetrics.patientsGrowth}
          icon={Activity}
          color="bg-orange-500"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimpleChart
          data={chartData.userGrowth}
          title="User Growth Trend"
          type="line"
        />
        <SimpleChart
          data={chartData.recordingsByTenant}
          title="Recordings by Tenant"
          type="bar"
        />
      </div>

      {/* Activity Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Daily Activity Overview</h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Recordings</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">User Logins</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Activity Level</th>
                </tr>
              </thead>
              <tbody>
                {chartData.dailyActivity.map((day, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-900">
                      {new Date(day.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <Heart className="h-4 w-4 text-red-500 mr-2" />
                        <span className="font-medium text-gray-900">{day.recordings}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-[#207CE5] mr-2" />
                        <span className="font-medium text-gray-900">{day.logins}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${Math.min((day.recordings / 200) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          {day.recordings > 150 ? 'High' : day.recordings > 100 ? 'Medium' : 'Low'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Platform Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Tenants</h3>
          <div className="space-y-3">
            {chartData.recordingsByTenant.slice(0, 3).map((tenant, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{tenant.tenant}</p>
                    <p className="text-sm text-gray-500">{tenant.recordings} recordings</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{tenant.percentage}%</p>
                  <p className="text-sm text-gray-500">of total</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">API Response Time</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                </div>
                <span className="text-sm font-medium text-gray-900">142ms</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Database Performance</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                </div>
                <span className="text-sm font-medium text-gray-900">92%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Storage Usage</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                </div>
                <span className="text-sm font-medium text-gray-900">78%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Error Rate</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '4%' }}></div>
                </div>
                <span className="text-sm font-medium text-gray-900">0.4%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAnalytics;